import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import FabricCard from "@/components/FabricCard";
import { SlidersHorizontal, X } from "lucide-react";

const swatch = (c) => String(c || "").trim().toLowerCase().replace(/\s+/g, "");

export default function FabricsCatalog() {
  const [fabrics, setFabrics] = useState([]);
  const [types, setTypes] = useState([]);
  const [selTypes, setSelTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [price, setPrice] = useState({ min: "", max: "" });
  const [sort, setSort] = useState("recent");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => { api.get("/fabric-types").then(({ data }) => setTypes(data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/fabrics").then(({ data }) => setFabrics(data || [])).catch(() => setFabrics([])); }, []);

  const facetColors = useMemo(() => [...new Set(fabrics.flatMap((f) => f.colors || []))].filter(Boolean).sort(), [fabrics]);
  const priceBounds = useMemo(() => { const ps = fabrics.map((f) => f.price_per_meter || 0); return ps.length ? [Math.min(...ps), Math.max(...ps)] : [0, 0]; }, [fabrics]);
  const toggle = (setter, arr, v) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const filtered = useMemo(() => {
    let items = fabrics.filter((f) => {
      if (selTypes.length && !selTypes.includes(f.fabric_type)) return false;
      if (colors.length && !(f.colors || []).some((c) => colors.includes(c))) return false;
      if (price.min !== "" && (f.price_per_meter || 0) < Number(price.min)) return false;
      if (price.max !== "" && (f.price_per_meter || 0) > Number(price.max)) return false;
      return true;
    });
    if (sort === "price_asc") items = [...items].sort((a, b) => a.price_per_meter - b.price_per_meter);
    if (sort === "price_desc") items = [...items].sort((a, b) => b.price_per_meter - a.price_per_meter);
    return items;
  }, [fabrics, selTypes, colors, price, sort]);

  const activeCount = selTypes.length + colors.length + (price.min !== "" || price.max !== "" ? 1 : 0);
  const clearAll = () => { setSelTypes([]); setColors([]); setPrice({ min: "", max: "" }); setSort("recent"); };

  const Sidebar = () => (
    <div className="bg-white border border-black/10 rounded-xl p-5" data-testid="fabrics-filters">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-xl">Filters</div>
        {activeCount > 0 && <button onClick={clearAll} data-testid="filter-clear" className="text-xs text-squire-jeans uppercase tracking-[0.15em]">Clear all</button>}
      </div>

      <FilterGroup label="Sort Results">
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans">
          <option value="recent">Recently Added</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
      </FilterGroup>

      <FilterGroup label="Price / mtr">
        <div className="flex items-center gap-2">
          <input type="number" value={price.min} onChange={(e) => setPrice({ ...price, min: e.target.value })} placeholder={priceBounds[0] ? `${priceBounds[0]}` : "min"} data-testid="filter-price-min" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
          <span className="text-[#9CA3AF]">-</span>
          <input type="number" value={price.max} onChange={(e) => setPrice({ ...price, max: e.target.value })} placeholder={priceBounds[1] ? `${priceBounds[1]}` : "max"} data-testid="filter-price-max" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-squire-jeans" />
        </div>
      </FilterGroup>

      {types.length > 0 && (
        <FilterGroup label="Fabric Type">
          <div className="space-y-1.5">
            {types.map((t) => (
              <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={selTypes.includes(t)} onChange={() => toggle(setSelTypes, selTypes, t)} data-testid={`filter-type-${t}`} className="accent-squire-jeans" /> {t}
              </label>
            ))}
          </div>
        </FilterGroup>
      )}

      {facetColors.length > 0 && (
        <FilterGroup label="Colour" last>
          <div className="space-y-1.5">
            {facetColors.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={colors.includes(c)} onChange={() => toggle(setColors, colors, c)} data-testid={`filter-color-${c}`} className="accent-squire-jeans" />
                <span className="w-3.5 h-3.5 rounded-full border border-black/20" style={{ background: swatch(c) }} /> {c}
              </label>
            ))}
          </div>
        </FilterGroup>
      )}
    </div>
  );

  return (
    <main className="pt-8 sq-container" data-testid="fabrics-catalog">
      <div className="border-b border-black/10 pb-6 mb-8">
        <div className="sq-eyebrow">The Cloth Library</div>
        <h1 className="font-display text-4xl md:text-5xl mt-2">Fabrics</h1>
        <p className="text-sm text-[#4B5563] mt-2">{filtered.length} fabrics · priced / mtr</p>
      </div>

      <div className="lg:hidden mb-4">
        <button onClick={() => setMobileOpen(true)} className="inline-flex items-center gap-2 border border-black/20 rounded-lg px-4 py-2 text-sm"><SlidersHorizontal size={16} /> Filters{activeCount ? ` (${activeCount})` : ""}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="hidden lg:block"><Sidebar /></aside>
        <div>
          {filtered.length === 0 ? (
            <div className="border border-black/10 rounded-xl p-16 text-center text-[#4B5563]">No fabrics match these filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
              {filtered.map((f) => <FabricCard key={f.id} fabric={f} />)}
            </div>
          )}
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-[#FAFAF8] overflow-y-auto p-4">
            <div className="flex justify-end"><button onClick={() => setMobileOpen(false)}><X size={22} /></button></div>
            <Sidebar />
            <button onClick={() => setMobileOpen(false)} className="sq-btn-primary w-full mt-4">Show {filtered.length} results</button>
          </div>
        </div>
      )}
    </main>
  );
}

function FilterGroup({ label, children, last }) {
  return (
    <div className={`${last ? "" : "border-b border-black/10 pb-4 mb-4"}`}>
      <div className="sq-eyebrow text-[#4B5563] mb-2">{label}</div>
      {children}
    </div>
  );
}
